import { useCallback, useMemo, useRef, useState } from 'react';
import { CJK_TEXT_STYLE, type DxfTextStyle, type ScaleSource } from '@drawing2d/core';
import {
  runPipeline,
  runRasterPipeline,
  describeFailure,
  downloadText,
  type RunResult,
  type RunFailure,
  type RasterRunResult,
} from './pipeline.js';
import { t, fill, ISSUE_LABELS, ERROR_LABELS, type Lang } from './i18n.js';
import { downloadNameFor } from './artefact-names.js';
import { DrawingBar } from './components/DrawingBar.js';
import { IdentityChain } from './components/IdentityChain.js';
import { InputCheck } from './components/InputCheck.js';
import { OverlayViewer } from './components/OverlayViewer.js';
import { Ledger } from './components/Ledger.js';
import { Compare } from './components/Compare.js';
import { ScaleStep } from './components/ScaleStep.js';
import { DxfFont } from './components/DxfFont.js';
import { RasterStep } from './components/RasterStep.js';

const SAMPLES = [
  { file: 'sample-a4.pdf', key: 'sampleA4' as const, paper: 'A4' as const },
  { file: 'sample-a3-cropped.pdf', key: 'sampleA3' as const, paper: 'A3' as const },
  { file: 'sample-rotated.pdf', key: 'sampleRotated' as const, paper: 'A4' as const },
];

const SCAN_SAMPLES = [
  { file: 'sample-scan.pdf', key: 'sampleScan' as const },
  { file: 'sample-hybrid.pdf', key: 'sampleHybrid' as const },
];

/**
 * Three surfaces, one application.
 *
 * Converting one drawing, comparing two versions of one drawing, and reading a
 * scan are separate jobs with separate state, and the mode switch keeps them that
 * way: only one is mounted at a time, so none can add a second file input, a second
 * overlay or a second set of marks to a page whose tests count exactly one of each.
 */
type Mode = 'convert' | 'compare' | 'scan';

export function App(): JSX.Element {
  const [mode, setMode] = useState<Mode>('convert');
  const [lang, setLang] = useState<Lang>('en');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<RunResult | null>(null);
  const [failure, setFailure] = useState<RunFailure | null>(null);
  const [bytes, setBytes] = useState<Uint8Array | null>(null);
  /**
   * The name of the file the drawing was read from.
   *
   * Held apart from `bytes` and never touched by `convert`, because resolving a
   * scale, calibrating, or choosing a different font all re-enter the pipeline
   * with the same bytes — and the drawing does not stop being that drawing when
   * it is converted a second time.
   */
  const [sourceName, setSourceName] = useState<string | null>(null);
  /**
   * Which drawing is loaded, as a number that changes only when a *different*
   * one arrives. It is the viewer's `key`.
   *
   * "Start over" used to set `result` to null, which unmounted the viewer and
   * took its page index, its zoom and its calibration points with it. Swapping
   * in place is better for the operator and removed that reset as a side effect,
   * so the new drawing inherited the last one's view — and page 2 of a one-page
   * drawing is `pages[1]`, which is undefined, so the backdrop, the overlay and
   * the page were all empty and the stage rendered blank.
   *
   * It must NOT change when the same drawing is converted again — resolving a
   * scale, calibrating, choosing a font — because the operator is mid-inspection
   * and throwing away their page and zoom there would be the same defect from
   * the other side.
   */
  const [drawingId, setDrawingId] = useState(0);
  const [drawingScale, setDrawingScale] = useState('1:1');
  /**
   * The font the DXF names. Held here rather than inside the step, because
   * changing it rewrites the DXF and so has to re-enter the pipeline — and
   * because the last scale the operator resolved has to survive that rewrite.
   */
  const [textStyle, setTextStyle] = useState<DxfTextStyle>(CJK_TEXT_STYLE);
  const [outlineText, setOutlineText] = useState(false);
  const [scaleUsed, setScaleUsed] = useState<ScaleSource | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  /** Scan state, held apart from the conversion's so neither disturbs the other. */
  const [scanResult, setScanResult] = useState<RasterRunResult | null>(null);
  const [scanBytes, setScanBytes] = useState<Uint8Array | null>(null);
  const [scanSourceName, setScanSourceName] = useState<string | null>(null);
  const [scanDpi, setScanDpi] = useState(300);
  const scanInput = useRef<HTMLInputElement>(null);

  const s = useCallback((k: Parameters<typeof t>[0]) => t(k, lang), [lang]);

  const scan = useCallback(async (data: Uint8Array, dpi: number) => {
    setBusy(true);
    setFailure(null);
    try {
      setScanResult(await runRasterPipeline(data, dpi));
      setScanBytes(data);
      setScanDpi(dpi);
    } catch (err) {
      setFailure(describeFailure(err));
      setScanResult(null);
    } finally {
      setBusy(false);
    }
  }, []);

  const convert = useCallback(
    async (
      data: Uint8Array,
      scale: ScaleSource | null,
      style: DxfTextStyle,
      outlines = false,
      /**
       * The name of a drawing being loaded for the first time.
       *
       * Absent on every re-conversion of the drawing already on screen. Applied
       * with the result rather than before it: set at the moment the file was
       * chosen, the bar read the new name for as long as the pipeline took while
       * every panel below it — and every download taken from one — was still the
       * previous drawing.
       */
      loaded?: string,
    ) => {
      setBusy(true);
      setFailure(null);
      try {
        const next = await runPipeline(data, scale, style, outlines);
        setResult(next);
        setBytes(data);
        setScaleUsed(scale);
        if (loaded !== undefined) {
          setSourceName(loaded);
          setDrawingId((n) => n + 1);
        }
      } catch (err) {
        setFailure(describeFailure(err));
        setResult(null);
      } finally {
        setBusy(false);
      }
    },
    [],
  );

  const onFile = useCallback(
    async (file: File) => {
      await convert(new Uint8Array(await file.arrayBuffer()), null, textStyle, outlineText, file.name);
    },
    [convert, textStyle, outlineText],
  );

  const onSample = useCallback(
    async (sample: (typeof SAMPLES)[number]) => {
      const response = await fetch(new URL(`fixtures/${sample.file}`, document.baseURI));
      await convert(
        new Uint8Array(await response.arrayBuffer()),
        null,
        textStyle,
        outlineText,
        sample.file,
      );
    },
    [convert, textStyle, outlineText],
  );

  /**
   * Open the picker. Do not clear anything first.
   *
   * The old path called `setResult(null)` and *then* `fileInput.current?.click()`,
   * which failed twice over: cancelling the dialog cost the operator the whole run
   * for a choice they declined to make, and the click was a no-op regardless —
   * the input was mounted inside `!result && !busy`, so the ref was null at the
   * moment it was read. The button silently dropped you on an empty dropzone.
   * The input is unconditional now and this only opens it; choosing a file
   * replaces the run, and cancelling costs nothing.
   */
  const loadAnother = useCallback(() => {
    setFailure(null);
    fileInput.current?.click();
  }, []);

  const loadAnotherScan = useCallback(() => {
    setFailure(null);
    scanInput.current?.click();
  }, []);

  /**
   * `failure` is one piece of state shared by three surfaces, so leaving it set
   * across a switch presents one surface's error as though another had just
   * refused a drawing nobody gave it.
   */
  const switchMode = useCallback((next: Mode) => {
    setMode(next);
    setFailure(null);
  }, []);

  /**
   * What went wrong, wherever it went wrong.
   *
   * It used to be rendered only inside the convert branch while `scan()` set the
   * same state — so a scan that threw returned the operator to an empty dropzone
   * with no message, no code and nothing to act on, and the error then appeared
   * on the conversion surface as a refusal of a drawing nobody had given it.
   */
  const failureSection = (retry: () => void): JSX.Element | false =>
    failure !== null && (
      <section className="failure">
        <h2>{s('failed')}</h2>
        <p className="code">{failure.code}</p>
        <p>{(ERROR_LABELS[failure.code] ?? ERROR_LABELS['UNEXPECTED']!)[lang]}</p>
        <p className="detail">{failure.message}</p>
        {/* A file that never opened still gets the named cause and the export
            setting that prevents it — the error code alone tells an operator
            what happened and nothing about what to do next. */}
        {failure.validation && (
          <InputCheck report={failure.validation} lang={lang} onUploadAgain={retry} />
        )}
        <button onClick={() => setFailure(null)}>{s('reset')}</button>
      </section>
    );

  const calibrate = useCallback(
    (pdfDistance: number, realMm: number) => {
      if (!bytes) return;
      void convert(bytes, { kind: 'calibration', pdfDistance, realMm }, textStyle, outlineText);
    },
    [bytes, convert, textStyle, outlineText],
  );

  const counts = useMemo(() => {
    if (!result) return [] as [string, number][];
    return Object.entries(result.metrics.entitiesByType).sort((x, y) => y[1] - x[1]);
  }, [result]);

  const entityTotal = counts.reduce((sum, [, n]) => sum + n, 0);
  const issueTotal = result
    ? Object.values(result.metrics.qualityIssuesByKind).reduce((sum, n) => sum + n, 0)
    : 0;

  return (
    <div className="app" data-lang={lang}>
      <header className="header">
        <div>
          <h1>{s('title')}</h1>
          <p className="sub">{s('subtitle')}</p>
        </div>
        <div className="header-right">
          <p className="privacy">{s('privacy')}</p>
          <div className="lang" role="group" aria-label="Language">
            <button className={lang === 'en' ? 'on' : ''} onClick={() => setLang('en')}>
              EN
            </button>
            <button className={lang === 'ja' ? 'on' : ''} onClick={() => setLang('ja')}>
              日本語
            </button>
          </div>
        </div>
      </header>

      <nav className="modes" role="group" aria-label="Mode">
        <button
          data-mode="convert"
          className={mode === 'convert' ? 'on' : ''}
          onClick={() => switchMode('convert')}
        >
          {s('modeConvert')}
        </button>
        <button
          data-mode="compare"
          className={mode === 'compare' ? 'on' : ''}
          onClick={() => switchMode('compare')}
        >
          {s('modeCompare')}
        </button>
        <button
          data-mode="scan"
          className={mode === 'scan' ? 'on' : ''}
          onClick={() => switchMode('scan')}
        >
          {s('modeScan')}
        </button>
      </nav>

      {mode === 'compare' && <Compare lang={lang} />}

      {mode === 'scan' && (
        <>
          <input
            ref={scanInput}
            type="file"
            accept="application/pdf,.pdf"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) {
                setScanSourceName(file.name);
                void file.arrayBuffer().then((b) => scan(new Uint8Array(b), scanDpi));
              }
            }}
          />
          {failureSection(loadAnotherScan)}

          {!scanResult && !busy && (
            <section
              className="dropzone"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const file = e.dataTransfer.files[0];
                if (file) {
                  setScanSourceName(file.name);
                  void file.arrayBuffer().then((b) => scan(new Uint8Array(b), scanDpi));
                }
              }}
            >
              <p className="drop-title">{s('scanDrop')}</p>
              <p className="note">{s('scanIntro')}</p>
              <button className="primary" onClick={() => scanInput.current?.click()}>
                {s('browse')}
              </button>
              <p className="or">{s('or')}</p>
              <div className="samples">
                {SCAN_SAMPLES.map((sample) => (
                  <button
                    key={sample.file}
                    onClick={() => {
                      setScanSourceName(sample.file);
                      void fetch(new URL(`fixtures/${sample.file}`, document.baseURI))
                        .then((r) => r.arrayBuffer())
                        .then((b) => scan(new Uint8Array(b), scanDpi));
                    }}
                  >
                    {s(sample.key)}
                  </button>
                ))}
              </div>
            </section>
          )}

          {busy && <p className="status">{s('scanWorking')}</p>}

          {scanResult && (
            <main className="result result-scan">
              {/*
                The panel is FIRST in the document, and the drawing follows it.
                That is the opposite of the conversion surface, and deliberately:
                there the outcome is exact and the drawing is the evidence, here the
                outcome is an estimate and what it is worth has to be read before
                the picture that makes it look convincing. `U5` in the first-use
                register is the same lesson -- what has to be read comes first --
                and on a wide screen the grid still puts the sheet on the left.
              */}
              <aside className="panels">
                <RasterStep
                  result={scanResult}
                  lang={lang}
                  dpi={scanDpi}
                  onDpi={(next) => {
                    setScanDpi(next);
                    if (scanBytes) void scan(scanBytes, next);
                  }}
                />
              </aside>
              {/*
                The same bar the conversion surface carries, in the same place and
                with the same two jobs: name the file, and offer another one. It is
                outside the `pageImage` test on purpose — a scan that rendered no
                image still has to say what it was and still has to let somebody
                load a different one.
              */}
              <section className="viewer scan-viewer">
                <div className="viewer-bar">
                  <DrawingBar
                    name={scanSourceName}
                    lang={lang}
                    onLoadAnother={loadAnotherScan}
                  />
                </div>
              {scanResult.pageImage !== null && (
                <div
                  className="scan-page"
                  /*
                    The same cap the conversion stage uses, for the same reason and
                    by the same means: a max-WIDTH derived from the sheet's own
                    ratio, never a max-height. A capped height with the width fixed
                    squashes the box. `--stage-cap` is what stops a tall sheet from
                    producing a page nobody can navigate -- without it this surface
                    measured 2.6 screens.
                  */
                  style={{
                    aspectRatio: `${scanResult.pageWidthPt} / ${scanResult.pageHeightPt}`,
                    maxWidth: `calc(var(--stage-cap) * ${scanResult.pageWidthPt} / ${scanResult.pageHeightPt})`,
                  }}
                >
                  <img src={scanResult.pageImage} alt={s('scanTitle')} />
                </div>
              )}
              </section>
            </main>
          )}
        </>
      )}

      {mode === 'convert' && (
        <>
      {/*
        Mounted whatever is on screen. It used to live inside the dropzone, which
        unmounts the moment a result exists — so every `fileInput.current?.click()`
        raised from a result surface read a null ref and did nothing at all.
      */}
      <input
        ref={fileInput}
        type="file"
        accept="application/pdf,.pdf"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          // Cleared so that choosing the same file twice still fires a change.
          e.target.value = '';
          if (file) void onFile(file);
        }}
      />
      {/*
        Above the dropzone, not below it. The dropzone reappearing is the
        consequence of the failure; the reason for it has to be the thing in
        front of the operator rather than a scroll underneath the thing they
        have already seen.
      */}
      {failureSection(loadAnother)}

      {!result && !busy && (
        <section
          className="dropzone"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            const file = e.dataTransfer.files[0];
            if (file) void onFile(file);
          }}
        >
          <p className="drop-title">{s('drop')}</p>
          <button className="primary" onClick={() => fileInput.current?.click()}>
            {s('browse')}
          </button>
          <p className="or">{s('or')}</p>
          <div className="samples">
            {SAMPLES.map((sample) => (
              <button key={sample.file} onClick={() => void onSample(sample)}>
                {s(sample.key)}
              </button>
            ))}
          </div>
        </section>
      )}

      {busy && <p className="status">{s('working')}</p>}

      {result && (
        <main className="result">
          <OverlayViewer
            key={drawingId}
            result={result}
            lang={lang}
            sourceName={sourceName}
            onCalibrate={calibrate}
            onLoadAnother={loadAnother}
          />

          {/*
            The outcome first, then the evidence for it.
            Previously this column opened with a paragraph about never guessing
            and closed, two screens down, with "DXF refused" in red among file
            hashes — so the one thing the operator came for was the last thing
            they found, and the diagnostics that explain how it was read were the
            visual peers of the download button. The order is now: what you get,
            what was read, how it was read.
          */}
          <aside className="panels">
            <ScaleStep
              result={result}
              lang={lang}
              drawingScale={drawingScale}
              onDrawingScale={setDrawingScale}
              onResolve={(source) => bytes && void convert(bytes, source, textStyle, outlineText)}
            />

            {/*
              Directly under the outcome, and above every diagnostic.

              It answers the question a reader has the moment they see what they
              got — should I trust this — and what the export threw away is
              visible in no figure printed below it. It is not placed *first*,
              because the outcome being the last thing on the page was U5 and
              this column exists in this order because of it.

              It is inside `.panels` rather than beside it: `.result` is a
              two-column grid, and a third child of it takes the right column's
              first row and pushes the diagnostics under the drawing. That cost
              the page 1.7 screens to 3.0 and is what the quality floor caught.
            */}
            <InputCheck
              report={result.validation}
              lang={lang}
              onUploadAgain={loadAnother}
            />

            {result.artefacts.dxf !== null && (
              <button
                className="primary wide download-dxf"
                onClick={() =>
                  downloadText(
                    downloadNameFor('drawing.dxf', sourceName),
                    result.artefacts.dxf ?? '',
                    'application/dxf',
                  )
                }
              >
                {s('dxfDownload')}
              </button>
            )}
            {result.document.scaleState === 'resolved' && (
              <>
                <DxfFont
                  lang={lang}
                  selected={textStyle}
                  missingGlyphs={result.outlines?.missing ?? []}
                  outlineText={outlineText}
                  onSelect={(style) => {
                    setTextStyle(style);
                    // Rewriting the DXF means re-entering the pipeline, and the
                    // resolved scale must survive it or the download disappears.
                    if (bytes) void convert(bytes, scaleUsed, style, outlineText);
                  }}
                  onOutlineText={(on) => {
                    setOutlineText(on);
                    if (bytes) void convert(bytes, scaleUsed, textStyle, on);
                  }}
                />
                <button className="link centred" onClick={() => bytes && void convert(bytes, null, textStyle, outlineText)}>
                  {s('changeScale')}
                </button>
              </>
            )}

            {/*
              The stamp is not folded. It carries the sentence that separates this
              from every other converter — nothing vanished uncounted — and it was
              a progress bar behind a fold, which is the least memorable rendering
              of the most distinctive fact on the page.
            */}
            <Ledger metrics={result.metrics} lang={lang} />

            {/* Directly after the operator ledger, because the two are halves of
                one claim: the ledger accounts for what came out of the PDF, and
                this accounts for what went into the DXF. Null when no DXF was
                written, since there is then nothing to have checked. */}
            {result.identity && <IdentityChain report={result.identity} lang={lang} />}

            <details className="fold" data-fold="read" open>
              <summary>
                {s('detailsRead')}
                <span className="fold-count">{fill(s('entitiesTotal'), { n: entityTotal })}</span>
              </summary>
              <ul className="counts">
                {counts.map(([type, n]) => (
                  <li key={type}>
                    <span>{type}</span>
                    <b>{n}</b>
                  </li>
                ))}
              </ul>
            </details>

            <details className="fold" data-fold="quality">
              <summary>
                {s('detailsQuality')}
                <span className="fold-count">
                  {issueTotal === 0
                    ? s('noneToReview')
                    : fill(s('issuesTotal'), { n: issueTotal })}
                </span>
              </summary>
              {issueTotal === 0 ? (
                <p className="note">{s('noIssues')}</p>
              ) : (
                <ul className="counts">
                  {Object.entries(result.metrics.qualityIssuesByKind).map(([kind, n]) => (
                    <li key={kind}>
                      <span>{(ISSUE_LABELS[kind] ?? { en: kind, ja: kind })[lang]}</span>
                      <b>{n}</b>
                    </li>
                  ))}
                </ul>
              )}
              {result.metrics.qualityIssuesByKind['outlined_text'] && (
                <p className="finding">{s('findingOutlined')}</p>
              )}
            </details>

            <details className="fold" data-fold="how">
              <summary>{s('detailsHow')}</summary>
              {(result.metrics.entitiesByType['CIRCLE'] ?? 0) +
                (result.metrics.entitiesByType['ARC'] ?? 0) >
                0 && <p className="finding">{s('findingCircle')}</p>}
              {(result.metrics.entitiesByType['MTEXT'] ?? 0) > 0 && (
                <p className="finding">{s('findingText')}</p>
              )}
            </details>

            <details className="fold" data-fold="files">
              <summary>
                {s('detailsFiles')}
                <span className="fold-count">{result.artefacts.manifests.length}</span>
              </summary>
              <ul className="artefacts">
                {result.artefacts.manifests.map((m) => (
                  <li key={m.storageKey}>
                    <div>
                      <b>{m.storageKey}</b>
                      <code>{m.sha256.slice(0, 16)}…</code>
                      <span className="bytes">{m.bytes.toLocaleString()} B</span>
                    </div>
                    <button
                      onClick={() =>
                        downloadText(
                          downloadNameFor(m.storageKey, sourceName),
                          contentFor(result, m.storageKey),
                          m.contentType,
                        )
                      }
                    >
                      {s('download')}
                    </button>
                  </li>
                ))}
              </ul>
              {result.artefacts.dxf === null && <p className="refused">{s('dxfRefused')}</p>}
              <p className="note">
                {s('elapsed')} {result.elapsedMs} ms
              </p>
            </details>

          </aside>
        </main>
      )}
        </>
      )}
    </div>
  );
}

function contentFor(result: RunResult, key: string): string {
  if (key === 'drawing.json') return result.artefacts.json;
  if (key === 'metrics.json') return result.artefacts.metrics;
  if (key === 'drawing.dxf') return result.artefacts.dxf ?? '';
  const match = /^overlay(?:\.p(\d+))?\.svg$/.exec(key);
  if (match) return result.artefacts.overlays[match[1] ? Number(match[1]) - 1 : 0] ?? '';
  return '';
}
